/**
 * AYU Phänotyp-Scoring-Algorithmus
 * 12 funktionelle Menschentypen mit 2-in-1 Logik
 * 
 * Gewichtung: Körper (body) 40%, Wohlbefinden (wellbeing) 30%, Lebensstil (lifestyle) 30%
 * 
 * Fragen-IDs:
 *   body:      b1–b40
 *   wellbeing: w1–w12
 *   lifestyle: ls1–ls10
 */

import type { SurveyResponse, SurveyAnswer } from "./surveySchema";

// ─── 12 AYU-Phänotypen (positive Beschreibungen) ───

export const PHENOTYPES = {
  dry_light: {
    id: "dry_light",
    name: "Heller Trockener",
    system: "Haut + Nervensystem",
    description: "Kreativ, beweglich und feinfühlig. Schnelle Auffassungsgabe, natürliche Leichtigkeit und bemerkenswerte Anpassungsfähigkeit.",
    cluster: "Haut & Nervensystem",
    empfehlungen: { ernaehrung: "Aufbauend, wärmend", bewegung: "Beruhigend (Yoga, Pilates)", mental: "Regelmäßigkeit, feste Tagesstruktur" },
    color: "#C4A265",
  },
  sensitive_red: {
    id: "sensitive_red",
    name: "Empfindlicher Rötlicher",
    system: "Haut + Entzündung",
    description: "Zielstrebig, analytisch und leidenschaftlich. Starke Willenskraft, scharfer Verstand und natürliche Führungsstärke.",
    cluster: "Entzündung & Regulation",
    empfehlungen: { ernaehrung: "Anti-Entzündung, kühlend", bewegung: "Ausgleichend kühlend (Schwimmen, Radfahren)", mental: "Natur, Musik, Humor" },
    color: "#D4644A",
  },
  oily_shiny: {
    id: "oily_shiny",
    name: "Fettiger Glänzender",
    system: "Stoffwechsel",
    description: "Vital, ausdauernd und genussvoll. Natürliche Vitalität, gute Regeneration und kräftiges Haar & Haut.",
    cluster: "Metabolismus & Struktur",
    empfehlungen: { ernaehrung: "Leichte Kost, Bitterstoffe", bewegung: "Leichte Aktivierung (Gehen, Schwimmen)", mental: "Aktivierende Routinen" },
    color: "#7B9E6B",
  },
  thin_nervous: {
    id: "thin_nervous",
    name: "Dünnhäutiger Nervöser",
    system: "Nervensystem",
    description: "Hochsensibel, intuitiv und schnell im Denken. Hohe Empathie, bemerkenswertes Denkvermögen und besondere Feinfühligkeit.",
    cluster: "Haut & Nervensystem",
    empfehlungen: { ernaehrung: "Aufbauend, wärmend", bewegung: "Beruhigend (Yoga, Pilates, KEIN HIIT)", mental: "Regelmäßigkeit, feste Tagesstruktur" },
    color: "#8B7EC8",
  },
  cyclic_reactive: {
    id: "cyclic_reactive",
    name: "Zyklisch Reagierender",
    system: "Hormonsystem",
    description: "Rhythmisch, wandlungsfähig und tiefgründig. Natürliches Rhythmusgefühl, emotionale Tiefe und bemerkenswerte Anpassungsfähigkeit.",
    cluster: "Entzündung & Regulation",
    empfehlungen: { ernaehrung: "Anti-Entzündung, kühlend, hormonausgleichend", bewegung: "Ausgleichend (Yoga, Schwimmen)", mental: "Zyklusbewusstsein, Rhythmus" },
    color: "#C87EA0",
  },
  heavy_stable: {
    id: "heavy_stable",
    name: "Dicker Stabiler",
    system: "Energiespeicherung",
    description: "Belastbar, ausdauernd und geerdet. Außergewöhnliche Belastbarkeit, innere Ruhe und beeindruckende Verlässlichkeit.",
    cluster: "Metabolismus & Struktur",
    empfehlungen: { ernaehrung: "Leichte Kost, Bitterstoffe", bewegung: "Leichte Aktivierung (Gehen, Schwimmen)", mental: "Aktivierende Routinen" },
    color: "#6B8E7B",
  },
  stress_driven: {
    id: "stress_driven",
    name: "Stressbedingter",
    system: "Stresssystem",
    description: "Engagiert, leistungsstark und verantwortungsbewusst. Hohe Leistungsbereitschaft, Durchhaltevermögen und bemerkenswertes Engagement.",
    cluster: "Stress",
    empfehlungen: { ernaehrung: "Aufbauend, wärmend, nervenberuhigend", bewegung: "Beruhigend (Yoga, Pilates, KEIN HIIT)", mental: "Abendrituale, Meditation, Journaling" },
    color: "#D4A574",
  },
  sensitive_allergic: {
    id: "sensitive_allergic",
    name: "Sensibel Allergischer",
    system: "Immunsystem",
    description: "Feinfühlig, achtsam und naturverbunden. Hohe Körperwahrnehmung, natürliche Achtsamkeit und Sensibilität als innerer Kompass.",
    cluster: "Immunsystem & Darm",
    empfehlungen: { ernaehrung: "Milieu-beruhigend, histaminarm", bewegung: "Sanfte Immunpflege (Qi Gong, Tai Chi)", mental: "Weniger Reizüberflutung, mehr Natur" },
    color: "#A0C4E8",
  },
  uv_sensitive: {
    id: "uv_sensitive",
    name: "UV-Sensibler",
    system: "Haut",
    description: "Aufmerksam, vorausschauend und bewusst. Vorausschauendes Denken, natürliche Selbstfürsorge und Aufmerksamkeit für Details.",
    cluster: "Entzündung & Regulation",
    empfehlungen: { ernaehrung: "Anti-Entzündung, kühlend, antioxidantienreich", bewegung: "Ausgleichend kühlend (Schwimmen, Indoor-Sport)", mental: "Natur, Musik, Humor" },
    color: "#E8D4A0",
  },
  genetic_loaded: {
    id: "genetic_loaded",
    name: "Genetisch Belasteter",
    system: "Familienanamnese",
    description: "Wissend, proaktiv und präventiv stark. Wertvoller Wissensvorsprung durch Kenntnis der familiären Geschichte. Prävention als Superkraft.",
    cluster: "Immunsystem & Darm",
    empfehlungen: { ernaehrung: "Milieu-beruhigend, präventiv", bewegung: "Sanfte Immunpflege (Qi Gong, Tai Chi)", mental: "Weniger Reizüberflutung, mehr Natur" },
    color: "#8B6B5A",
  },
  digestive_sensitive: {
    id: "digestive_sensitive",
    name: "Verdauungssensibler",
    system: "Darm",
    description: "Körperbewusst, intuitiv essend und achtsam. Starkes Körperbewusstsein, intuitive Ernährung und bemerkenswerte Selbstkenntnis.",
    cluster: "Immunsystem & Darm",
    empfehlungen: { ernaehrung: "Milieu-beruhigend, histaminarm, leicht verdaulich", bewegung: "Sanfte Immunpflege (Qi Gong, Tai Chi)", mental: "Weniger Reizüberflutung, mehr Natur" },
    color: "#C4B265",
  },
  metabolic_sensitive: {
    id: "metabolic_sensitive",
    name: "Stoffwechselsensibler",
    system: "Metabolismus",
    description: "Strukturiert, diszipliniert und vorausplanend. Natürliche Disziplin, strukturiertes Denken und bemerkenswerte Körperintelligenz.",
    cluster: "Metabolismus & Struktur",
    empfehlungen: { ernaehrung: "Leichte Kost, Bitterstoffe, blutzuckerregulierend", bewegung: "Leichte Aktivierung (Gehen, Schwimmen)", mental: "Aktivierende Routinen" },
    color: "#7BC4A0",
  },
} as const;

export type PhenotypeId = keyof typeof PHENOTYPES;

// ─── Scoring-Matrix: Frage → Phänotyp-Punkte ───
// Sektionen: body (40%), wellbeing (30%), lifestyle (30%)

interface ScoringRule {
  questionId: string;
  section: "body" | "wellbeing" | "lifestyle";
  mappings: {
    value: string | string[] | number;
    condition?: "gt" | "lt" | "eq" | "includes";
    scores: Partial<Record<PhenotypeId, number>>;
  }[];
}

const SCORING_RULES: ScoringRule[] = [
  // ─── BODY (40% weight) ───
  // b5: Körperbau
  {
    questionId: "b5",
    section: "body",
    mappings: [
      { value: "slim", scores: { dry_light: 3, thin_nervous: 2 } },
      { value: "athletic", scores: { sensitive_red: 2 } },
      { value: "medium", scores: { metabolic_sensitive: 1 } },
      { value: "sturdy", scores: { heavy_stable: 2, oily_shiny: 1 } },
      { value: "heavy", scores: { heavy_stable: 3, oily_shiny: 2 } },
    ],
  },
  // b6: Gewichtsveränderung
  {
    questionId: "b6",
    section: "body",
    mappings: [
      { value: "gain_easy", scores: { heavy_stable: 2, oily_shiny: 2, metabolic_sensitive: 1 } },
      { value: "lose_easy", scores: { dry_light: 2, thin_nervous: 2 } },
      { value: "stable", scores: { heavy_stable: 1 } },
      { value: "fluctuate", scores: { cyclic_reactive: 2, metabolic_sensitive: 2 } },
    ],
  },
  // b11: Verdauung nach dem Essen
  {
    questionId: "b11",
    section: "body",
    mappings: [
      { value: ["tired"], condition: "includes", scores: { heavy_stable: 2, metabolic_sensitive: 1 } },
      { value: ["bloated"], condition: "includes", scores: { digestive_sensitive: 3, metabolic_sensitive: 1 } },
      { value: ["heavy"], condition: "includes", scores: { heavy_stable: 2, digestive_sensitive: 1 } },
      { value: ["acidic"], condition: "includes", scores: { sensitive_red: 2, digestive_sensitive: 2 } },
      { value: ["energized"], condition: "includes", scores: {} },
    ],
  },
  // b12: Stuhlgang
  {
    questionId: "b12",
    section: "body",
    mappings: [
      { value: "constipation", scores: { dry_light: 2, digestive_sensitive: 2 } },
      { value: "loose", scores: { sensitive_red: 2, digestive_sensitive: 2 } },
      { value: "alternating", scores: { digestive_sensitive: 3, stress_driven: 1 } },
      { value: "irregular", scores: { digestive_sensitive: 2, thin_nervous: 1 } },
    ],
  },
  // b13: Blähungen
  {
    questionId: "b13",
    section: "body",
    mappings: [
      { value: "often", scores: { digestive_sensitive: 2, dry_light: 1 } },
      { value: "always", scores: { digestive_sensitive: 3, dry_light: 2 } },
      { value: "sometimes", scores: { digestive_sensitive: 1 } },
    ],
  },
  // b14: Unverträglichkeiten
  {
    questionId: "b14",
    section: "body",
    mappings: [
      { value: "yes", scores: { sensitive_allergic: 3, digestive_sensitive: 2 } },
    ],
  },
  // b15: Hauttyp
  {
    questionId: "b15",
    section: "body",
    mappings: [
      { value: "dry", scores: { dry_light: 3 } },
      { value: "oily", scores: { oily_shiny: 3 } },
      { value: "sensitive", scores: { sensitive_red: 3, uv_sensitive: 1 } },
      { value: "combination", scores: { cyclic_reactive: 1 } },
    ],
  },
  // b16: Hautprobleme
  {
    questionId: "b16",
    section: "body",
    mappings: [
      { value: ["acne"], condition: "includes", scores: { oily_shiny: 2, sensitive_red: 1 } },
      { value: ["eczema"], condition: "includes", scores: { sensitive_allergic: 3, sensitive_red: 2 } },
      { value: ["redness"], condition: "includes", scores: { sensitive_red: 3 } },
      { value: ["dryness"], condition: "includes", scores: { dry_light: 2 } },
      { value: ["sun_sensitivity"], condition: "includes", scores: { uv_sensitive: 3 } },
      { value: ["pigmentation"], condition: "includes", scores: { uv_sensitive: 2 } },
    ],
  },
  // b17: Temperaturempfinden
  {
    questionId: "b17",
    section: "body",
    mappings: [
      { value: "cold_sensitive", scores: { dry_light: 2, thin_nervous: 2 } },
      { value: "heat_sensitive", scores: { sensitive_red: 2, uv_sensitive: 1 } },
      { value: "fluctuating", scores: { cyclic_reactive: 2 } },
    ],
  },
  // b18: Allergien
  {
    questionId: "b18",
    section: "body",
    mappings: [
      { value: "yes", scores: { sensitive_allergic: 3 } },
    ],
  },
  // b19: Infektanfälligkeit
  {
    questionId: "b19",
    section: "body",
    mappings: [
      { value: "often", scores: { sensitive_allergic: 2, thin_nervous: 1 } },
      { value: "chronic", scores: { sensitive_allergic: 3, genetic_loaded: 1 } },
    ],
  },
  // b20: Autoimmun
  {
    questionId: "b20",
    section: "body",
    mappings: [
      { value: "yes", scores: { genetic_loaded: 3, sensitive_allergic: 2 } },
    ],
  },
  // b22: Schlafqualität (range 1-10)
  {
    questionId: "b22",
    section: "body",
    mappings: [
      { value: 4, condition: "lt", scores: { thin_nervous: 2, stress_driven: 2 } },
      { value: 6, condition: "lt", scores: { stress_driven: 1 } },
    ],
  },
  // b23: Einschlafprobleme
  {
    questionId: "b23",
    section: "body",
    mappings: [
      { value: "often", scores: { thin_nervous: 2, stress_driven: 2 } },
      { value: "always", scores: { thin_nervous: 3, stress_driven: 2 } },
    ],
  },
  // b24: Durchschlafprobleme
  {
    questionId: "b24",
    section: "body",
    mappings: [
      { value: "often", scores: { thin_nervous: 2, stress_driven: 1 } },
      { value: "always", scores: { thin_nervous: 3, stress_driven: 2 } },
    ],
  },

  // ─── WELLBEING (30% weight) ───
  // w1: Stresslevel (range 1-10)
  {
    questionId: "w1",
    section: "wellbeing",
    mappings: [
      { value: 7, condition: "gt", scores: { stress_driven: 3, thin_nervous: 1 } },
      { value: 5, condition: "gt", scores: { stress_driven: 1 } },
    ],
  },
  // w2: Stressreaktion
  {
    questionId: "w2",
    section: "wellbeing",
    mappings: [
      { value: "anxious", scores: { thin_nervous: 3, dry_light: 1 } },
      { value: "angry", scores: { sensitive_red: 2, stress_driven: 1 } },
      { value: "withdrawn", scores: { heavy_stable: 1, stress_driven: 1 } },
      { value: "overthinking", scores: { thin_nervous: 2, stress_driven: 1 } },
      { value: "physical", scores: { stress_driven: 3, digestive_sensitive: 1 } },
    ],
  },
  // w3: Stimmung
  {
    questionId: "w3",
    section: "wellbeing",
    mappings: [
      { value: "low", scores: { stress_driven: 2, heavy_stable: 1 } },
      { value: "anxious", scores: { thin_nervous: 3 } },
      { value: "fluctuating", scores: { cyclic_reactive: 3, thin_nervous: 1 } },
    ],
  },
  // w4: Emotionale Stabilität (range 1-10)
  {
    questionId: "w4",
    section: "wellbeing",
    mappings: [
      { value: 4, condition: "lt", scores: { cyclic_reactive: 2, thin_nervous: 2 } },
    ],
  },
  // w5: Konzentration (range 1-10)
  {
    questionId: "w5",
    section: "wellbeing",
    mappings: [
      { value: 4, condition: "lt", scores: { thin_nervous: 2, stress_driven: 1 } },
    ],
  },
  // w6: Gedankenkarussell
  {
    questionId: "w6",
    section: "wellbeing",
    mappings: [
      { value: "often", scores: { thin_nervous: 2, stress_driven: 1 } },
      { value: "always", scores: { thin_nervous: 3, stress_driven: 2 } },
    ],
  },
  // w7: Dominante Emotion
  {
    questionId: "w7",
    section: "wellbeing",
    mappings: [
      { value: "worry", scores: { thin_nervous: 2, dry_light: 1 } },
      { value: "anger", scores: { sensitive_red: 2 } },
      { value: "sadness", scores: { heavy_stable: 1, stress_driven: 1 } },
      { value: "restlessness", scores: { thin_nervous: 3, dry_light: 1 } },
      { value: "numbness", scores: { heavy_stable: 2 } },
    ],
  },
  // w9: Energielevel (range 1-10) — low energy
  {
    questionId: "w9",
    section: "wellbeing",
    mappings: [
      { value: 3, condition: "lt", scores: { heavy_stable: 2, stress_driven: 2 } },
      { value: 5, condition: "lt", scores: { stress_driven: 1 } },
    ],
  },
  // w10: Erschöpfung
  {
    questionId: "w10",
    section: "wellbeing",
    mappings: [
      { value: "often", scores: { stress_driven: 2, thin_nervous: 1 } },
      { value: "always", scores: { stress_driven: 3, thin_nervous: 2 } },
    ],
  },
  // w11: Morgens
  {
    questionId: "w11",
    section: "wellbeing",
    mappings: [
      { value: "tired", scores: { stress_driven: 1, thin_nervous: 1 } },
      { value: "heavy", scores: { heavy_stable: 2, oily_shiny: 1 } },
      { value: "anxious", scores: { thin_nervous: 2 } },
    ],
  },

  // ─── LIFESTYLE (30% weight) ───
  // ls1: Aufstehzeit
  {
    questionId: "ls1",
    section: "lifestyle",
    mappings: [
      { value: "after_9", scores: { heavy_stable: 1, oily_shiny: 1 } },
      { value: "before_6", scores: { dry_light: 1 } },
    ],
  },
  // ls3: Morgenroutine
  {
    questionId: "ls3",
    section: "lifestyle",
    mappings: [
      { value: "none", scores: { thin_nervous: 1, stress_driven: 1 } },
      { value: "varies", scores: { cyclic_reactive: 1 } },
    ],
  },
  // ls4: Arbeitsbelastung
  {
    questionId: "ls4",
    section: "lifestyle",
    mappings: [
      { value: "high", scores: { stress_driven: 2 } },
      { value: "overwhelming", scores: { stress_driven: 3, thin_nervous: 1 } },
    ],
  },
  // ls5: Zeitdruck
  {
    questionId: "ls5",
    section: "lifestyle",
    mappings: [
      { value: "often", scores: { stress_driven: 2 } },
      { value: "always", scores: { stress_driven: 3 } },
    ],
  },
  // ls7: Erholung (range 1-10)
  {
    questionId: "ls7",
    section: "lifestyle",
    mappings: [
      { value: 4, condition: "lt", scores: { stress_driven: 2, thin_nervous: 1 } },
    ],
  },
  // ls8: Work-Life-Balance (range 1-10)
  {
    questionId: "ls8",
    section: "lifestyle",
    mappings: [
      { value: 4, condition: "lt", scores: { stress_driven: 2 } },
    ],
  },
  // ls9: Lebensqualität (range 1-10)
  {
    questionId: "ls9",
    section: "lifestyle",
    mappings: [
      { value: 4, condition: "lt", scores: { stress_driven: 1, heavy_stable: 1 } },
    ],
  },
  // ─── NEUE FRAGEN (b29-b40) ───
  // b30: Probiotika
  {
    questionId: "b30",
    section: "body",
    mappings: [
      { value: "never", scores: { digestive_sensitive: 1 } },
    ],
  },
  // b31: Kaiserschnitt
  {
    questionId: "b31",
    section: "body",
    mappings: [
      { value: "yes", scores: { digestive_sensitive: 1, sensitive_allergic: 1 } },
    ],
  },
  // b32: Alkohol
  {
    questionId: "b32",
    section: "body",
    mappings: [
      { value: "several_weekly", scores: { metabolic_sensitive: 1, oily_shiny: 1 } },
      { value: "daily", scores: { metabolic_sensitive: 2, oily_shiny: 1 } },
    ],
  },
  // b33: Rauchen
  {
    questionId: "b33",
    section: "body",
    mappings: [
      { value: "occasionally", scores: { stress_driven: 1 } },
      { value: "regularly", scores: { stress_driven: 2, sensitive_red: 1 } },
    ],
  },
  // b34: Chronisch-entzündliche Darmerkrankung
  {
    questionId: "b34",
    section: "body",
    mappings: [
      { value: "yes", scores: { digestive_sensitive: 3, sensitive_allergic: 2 } },
    ],
  },
  // b35: Diagnostizierte Erkrankungen
  {
    questionId: "b35",
    section: "body",
    mappings: [
      { value: ["diabetes"], condition: "includes", scores: { metabolic_sensitive: 3, genetic_loaded: 2 } },
      { value: ["thyroid"], condition: "includes", scores: { metabolic_sensitive: 2, cyclic_reactive: 2 } },
      { value: ["hypertension"], condition: "includes", scores: { stress_driven: 2, genetic_loaded: 1 } },
      { value: ["heart"], condition: "includes", scores: { genetic_loaded: 3, stress_driven: 1 } },
      { value: ["autoimmune"], condition: "includes", scores: { genetic_loaded: 3, sensitive_allergic: 2 } },
      { value: ["depression_anxiety"], condition: "includes", scores: { thin_nervous: 2, stress_driven: 2 } },
      { value: ["cancer"], condition: "includes", scores: { genetic_loaded: 3 } },
    ],
  },
  // b36: Antibiotika
  {
    questionId: "b36",
    section: "body",
    mappings: [
      { value: "recently", scores: { digestive_sensitive: 2, sensitive_allergic: 1 } },
      { value: "past_year", scores: { digestive_sensitive: 1 } },
    ],
  },
  // b37: Infektionskrankheiten
  {
    questionId: "b37",
    section: "body",
    mappings: [
      { value: "yes", scores: { sensitive_allergic: 2, genetic_loaded: 1 } },
    ],
  },
  // b38: Lebensmittel-Präferenzen
  {
    questionId: "b38",
    section: "body",
    mappings: [
      { value: ["processed"], condition: "includes", scores: { metabolic_sensitive: 2, oily_shiny: 1 } },
      { value: ["sweets"], condition: "includes", scores: { metabolic_sensitive: 1 } },
      { value: ["fermented"], condition: "includes", scores: { digestive_sensitive: -1 } },
    ],
  },
  // b39: Ernährungsfrequenz
  {
    questionId: "b39",
    section: "body",
    mappings: [
      { value: ["meat_daily"], condition: "includes", scores: { metabolic_sensitive: 2, oily_shiny: 1 } },
      { value: ["dairy_rarely"], condition: "includes", scores: { dry_light: 1 } },
      { value: ["fruit_veg_rarely"], condition: "includes", scores: { metabolic_sensitive: 1 } },
    ],
  },
  // b40: Reisen
  {
    questionId: "b40",
    section: "body",
    mappings: [
      { value: "yes", scores: { sensitive_allergic: 1 } },
    ],
  },

  // ─── LIFESTYLE (30% weight) ───
  // ls10: Familiäre Erkrankungen
  {
    questionId: "ls10",
    section: "lifestyle",
    mappings: [
      { value: ["diabetes"], condition: "includes", scores: { metabolic_sensitive: 3, genetic_loaded: 2 } },
      { value: ["heart"], condition: "includes", scores: { genetic_loaded: 3, stress_driven: 1 } },
      { value: ["cancer"], condition: "includes", scores: { genetic_loaded: 3 } },
      { value: ["autoimmune"], condition: "includes", scores: { genetic_loaded: 3, sensitive_allergic: 2 } },
      { value: ["digestive"], condition: "includes", scores: { digestive_sensitive: 2, genetic_loaded: 1 } },
      { value: ["allergies"], condition: "includes", scores: { sensitive_allergic: 2, genetic_loaded: 1 } },
      { value: ["skin"], condition: "includes", scores: { uv_sensitive: 1, genetic_loaded: 1 } },
      { value: ["mental"], condition: "includes", scores: { stress_driven: 1, thin_nervous: 1 } },
    ],
  },
];

// ─── Scoring-Engine ───

interface PhenotypeScore {
  phenotypeId: PhenotypeId;
  totalScore: number;
  bodyScore: number;
  wellbeingScore: number;
  lifestyleScore: number;
  sourceCount: number;
  consistentHints: number;
}

export interface PhenotypeResult {
  primary: PhenotypeId;
  secondary: PhenotypeId | null;
  isTwoInOne: boolean;
  scores: PhenotypeScore[];
  confidence: "high" | "medium" | "low";
  explanation: string[];
}

function matchesCondition(
  answerValue: string | number | string[],
  ruleValue: string | string[] | number,
  condition?: "gt" | "lt" | "eq" | "includes"
): boolean {
  if (condition === "includes") {
    if (Array.isArray(answerValue) && Array.isArray(ruleValue)) {
      return ruleValue.some((rv) => answerValue.includes(rv));
    }
    if (Array.isArray(answerValue)) {
      return answerValue.includes(String(ruleValue));
    }
    return false;
  }

  if (condition === "gt") {
    return Number(answerValue) > Number(ruleValue);
  }
  if (condition === "lt") {
    return Number(answerValue) < Number(ruleValue);
  }
  if (condition === "eq") {
    return String(answerValue) === String(ruleValue);
  }

  // Default: exact match
  return String(answerValue) === String(ruleValue);
}

export function calculatePhenotype(response: SurveyResponse): PhenotypeResult {
  // Initialize scores per phenotype per section
  const rawScores: Record<PhenotypeId, { body: number; wellbeing: number; lifestyle: number; hints: number }> = {} as any;

  for (const id of Object.keys(PHENOTYPES) as PhenotypeId[]) {
    rawScores[id] = { body: 0, wellbeing: 0, lifestyle: 0, hints: 0 };
  }

  // Collect all answers in a flat map
  const answerMap = new Map<string, SurveyAnswer>();
  if (response.goals) for (const a of response.goals) answerMap.set(a.questionId, a);
  if (response.body) for (const a of response.body) answerMap.set(a.questionId, a);
  if (response.wellbeing) for (const a of response.wellbeing) answerMap.set(a.questionId, a);
  if (response.lifestyle) for (const a of response.lifestyle) answerMap.set(a.questionId, a);

  // Apply scoring rules
  for (const rule of SCORING_RULES) {
    const answer = answerMap.get(rule.questionId);
    if (!answer) continue;

    for (const mapping of rule.mappings) {
      if (matchesCondition(answer.value, mapping.value, mapping.condition)) {
        for (const [phenoId, points] of Object.entries(mapping.scores)) {
          const pid = phenoId as PhenotypeId;
          rawScores[pid][rule.section] += points!;
          rawScores[pid].hints += 1;
        }
      }
    }
  }

  // Apply weights: Body 40%, Wellbeing 30%, Lifestyle 30%
  const phenotypeScores: PhenotypeScore[] = (Object.keys(PHENOTYPES) as PhenotypeId[]).map((id) => {
    const raw = rawScores[id];
    const totalScore =
      raw.body * 0.4 +
      raw.wellbeing * 0.3 +
      raw.lifestyle * 0.3;

    let sourceCount = 0;
    if (raw.body > 0) sourceCount++;
    if (raw.wellbeing > 0) sourceCount++;
    if (raw.lifestyle > 0) sourceCount++;

    return {
      phenotypeId: id,
      totalScore,
      bodyScore: raw.body,
      wellbeingScore: raw.wellbeing,
      lifestyleScore: raw.lifestyle,
      sourceCount,
      consistentHints: raw.hints,
    };
  });

  // Sort by total score descending
  phenotypeScores.sort((a, b) => b.totalScore - a.totalScore);

  const top = phenotypeScores[0];
  const second = phenotypeScores[1];

  // Determine if 2-in-1
  let isTwoInOne = false;
  let secondary: PhenotypeId | null = null;

  if (top.totalScore > 0 && second.totalScore > 0) {
    const diff = top.totalScore - second.totalScore;
    const diffPercent = diff / top.totalScore;

    const maxPossible = 30;
    const topPercent = top.totalScore / maxPossible;
    const secondPercent = second.totalScore / maxPossible;

    if (diffPercent < 0.1 || (topPercent > 0.7 && secondPercent > 0.7)) {
      if (top.sourceCount >= 2 && second.sourceCount >= 2) {
        isTwoInOne = true;
        secondary = second.phenotypeId;
      }
    }

    if (!secondary && second.sourceCount >= 2 && second.consistentHints >= 5) {
      secondary = second.phenotypeId;
    }
  }

  // Confidence level
  let confidence: "high" | "medium" | "low" = "low";
  if (top.consistentHints >= 7 && top.sourceCount >= 2) {
    confidence = "high";
  } else if (top.consistentHints >= 5 && top.sourceCount >= 2) {
    confidence = "medium";
  }

  // Build explanation (positive)
  const explanation: string[] = [];
  const primaryInfo = PHENOTYPES[top.phenotypeId];
  explanation.push(`Dein primärer Phänotyp ist der "${primaryInfo.name}" (${primaryInfo.system}).`);
  explanation.push(primaryInfo.description);

  if (isTwoInOne && secondary) {
    const secondaryInfo = PHENOTYPES[secondary];
    explanation.push(`Du zeigst zusätzlich starke Merkmale des "${secondaryInfo.name}" (${secondaryInfo.system}).`);
    explanation.push(`Dies ergibt einen 2-in-1 Phänotyp: ${primaryInfo.name} / ${secondaryInfo.name}.`);
  } else if (secondary) {
    const secondaryInfo = PHENOTYPES[secondary];
    explanation.push(`Dein sekundärer Phänotyp ist der "${secondaryInfo.name}".`);
  }

  return {
    primary: top.phenotypeId,
    secondary,
    isTwoInOne,
    scores: phenotypeScores,
    confidence,
    explanation,
  };
}

/**
 * Get a human-readable phenotype label for display.
 */
export function getPhenotypeLabel(result: PhenotypeResult): string {
  const primary = PHENOTYPES[result.primary].name;
  if (result.isTwoInOne && result.secondary) {
    const secondary = PHENOTYPES[result.secondary].name;
    return `${primary} / ${secondary}`;
  }
  return primary;
}

/**
 * Get coaching style based on phenotype cluster.
 * Maps functional clusters to coaching approaches.
 */
export type CoachingStyle =
  | "calming"      // Beruhigend, erdend (Haut & Nervensystem)
  | "cooling"      // Kühlend, anti-entzündlich (Entzündung & Regulation)
  | "activating"   // Aktivierend, leicht (Metabolismus & Struktur)
  | "grounding"    // Erdend, stressreduzierend (Stress)
  | "soothing";    // Milieu-beruhigend, immunstärkend (Immunsystem & Darm)

export function getCoachingStyle(result: PhenotypeResult): CoachingStyle {
  const primary = PHENOTYPES[result.primary];

  if (primary.cluster === "Haut & Nervensystem") return "calming";
  if (primary.cluster === "Entzündung & Regulation") return "cooling";
  if (primary.cluster === "Metabolismus & Struktur") return "activating";
  if (primary.cluster === "Stress") return "grounding";
  if (primary.cluster === "Immunsystem & Darm") return "soothing";
  return "soothing";
}
